import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Pen, Type, RotateCcw } from 'lucide-react';

/**
 * SignaturePad — typed (default) + drawn (canvas) toggle.
 * Emits { signature_type: 'typed'|'drawn', signature_value: string }
 * via onChange. Drawn signatures are emitted as data:image/png;base64,...
 *
 * Designed to work cleanly on touch + mouse, mobile + desktop, with no
 * external library dependency (keeps bundle small).
 */
export const SignaturePad = ({ value, onChange, defaultName = '', testIdPrefix = 'sig' }) => {
  const [mode, setMode] = useState(value?.signature_type === 'drawn' ? 'drawn' : 'typed');
  const [typedName, setTypedName] = useState(value?.signature_type === 'typed' ? (value?.signature_value || defaultName) : defaultName);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasDrawing, setHasDrawing] = useState(false);

  // Keep typed value in sync with parent
  useEffect(() => {
    if (mode === 'typed') {
      onChange?.({ signature_type: 'typed', signature_value: typedName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedName, mode]);

  // Restore previous drawing if value provided
  useEffect(() => {
    if (mode !== 'drawn' || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    if (value?.signature_type === 'drawn' && value?.signature_value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvasRef.current.width, canvasRef.current.height);
        setHasDrawing(true);
      };
      img.src = value.signature_value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const getPos = (evt) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (evt.touches?.[0]?.clientX ?? evt.clientX) - rect.left;
    const y = (evt.touches?.[0]?.clientY ?? evt.clientY) - rect.top;
    return {
      x: x * (canvasRef.current.width / rect.width),
      y: y * (canvasRef.current.height / rect.height),
    };
  };

  const startDraw = (evt) => {
    evt.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPos(evt);
  };

  const draw = (evt) => {
    if (!drawingRef.current) return;
    evt.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(evt);
    ctx.strokeStyle = '#0d2b53';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPointRef.current = { x, y };
    setHasDrawing(true);
  };

  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onChange?.({ signature_type: 'drawn', signature_value: dataUrl });
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasDrawing(false);
    onChange?.({ signature_type: 'drawn', signature_value: '' });
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-pad`}>
      <Tabs value={mode} onValueChange={(v) => { setMode(v); }}>
        <TabsList className="grid grid-cols-2 w-full max-w-sm">
          <TabsTrigger value="typed" data-testid={`${testIdPrefix}-tab-typed`}>
            <Type className="w-4 h-4 mr-1" /> Type
          </TabsTrigger>
          <TabsTrigger value="drawn" data-testid={`${testIdPrefix}-tab-drawn`}>
            <Pen className="w-4 h-4 mr-1" /> Draw
          </TabsTrigger>
        </TabsList>

        <TabsContent value="typed">
          <div>
            <Input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full legal name"
              data-testid={`${testIdPrefix}-typed-input`}
            />
            {typedName && (
              <div className="mt-2 p-3 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded text-center bg-gray-50 dark:bg-slate-900">
                <span className="italic text-2xl text-blue-900 dark:text-blue-200" style={{ fontFamily: 'cursive, "Brush Script MT"' }}>
                  {typedName}
                </span>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              By typing your name above, you are providing a legally-binding electronic signature
              with the same effect as a handwritten signature.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="drawn">
          <div className="border-2 border-dashed border-gray-300 dark:border-slate-700 rounded bg-white dark:bg-slate-50">
            <canvas
              ref={canvasRef}
              width={600}
              height={180}
              className="w-full touch-none cursor-crosshair"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
              data-testid={`${testIdPrefix}-canvas`}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">Sign with your mouse or finger above.</p>
            <Button type="button" variant="outline" size="sm" onClick={clearCanvas} data-testid={`${testIdPrefix}-clear-btn`}>
              <RotateCcw className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </div>
          {!hasDrawing && (
            <p className="text-xs text-amber-600 mt-1">No signature drawn yet — please sign before submitting.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SignaturePad;
